// src/pages/manifest/PickupManifest.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import {
    RefreshCw,
    Truck,
    ListChecks,
    ShieldCheck,
    FileUp,
    Download,
    Search,
    AlertCircle,
    ChevronDown,
    ChevronRight,
    X as XIcon,
    ScanLine,
    Hash as HashIcon,
    UserCircle2,
    MapPin,
    Package,
    CalendarClock,
    Rows3,
    Zap,
    Info,
    ZoomIn,
    ZoomOut,
    Filter,
    TrendingUp,
    Clock,
    CheckCircle2,
    Grid3x3,
    LayoutList,
    MoreVertical,
    Copy,
    ExternalLink,
    Archive,
    Sparkles,
    ArrowUpRight,
    Eye,
    EyeOff,
    Settings2,
    Star,
    BarChart3,
    Plus,
    Minus,
} from "lucide-react";
import { API } from "../../utils/apiUrls";
import { getOrderStatusName } from "../../utils/enums";
import CustomDateRangePicker from "../../utils/CustomDateRangePicker";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

const DEFAULT_PAGE_SIZE = 10;
const MANIFEST_AWB_PAGE_SIZE = 60;

const idemKey = () => `idem-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const getAuth = () => {
    const Authorization = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!Authorization) throw new Error("Not authenticated");
    return { Authorization, merchantId };
};

const keyOf = (s = {}) => {
    const parts = [
        s.shipment_order_id,
        s.shipment_order_number,
        s.order_id,
        s.order_reference_number,
        s.awb_number,
        s.id,
    ]
        .filter((x) => x !== undefined && x !== null)
        .map((x) => String(x).trim())
        .filter(Boolean);
    if (parts.length) return parts.join("|");
    if (!s._row_uid) s._row_uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return s._row_uid;
};

const ALLOWED_MANIFEST_STATUS_CODES = Object.freeze([
    "270", // AWB_REGISTERED
    "30", // ORDER_PLACED
    "40", // PICKUP_PENDING
    "240", // OUT_FOR_PICKUP
    "50", // PICKUP_FAILED
]);

const parseApiError = (err) => {
    const data = err?.response?.data || {};
    const status = err?.response?.status;
    const code = data.errorCode || data.error_code || "UNEXPECTED_ERROR";
    const msg =
        data.message ||
        data.error_message ||
        (status === 404
            ? "The requested resource was not found."
            : "Something went wrong. Please try again or contact support.");
    const ctx = data.errorContext || data.error_context || null;
    return { title: "Request failed", message: msg, code, status, context: ctx };
};

const lottieSrc = "/animations/loader.lottie";
function LottieLoader({ message = "Loading..." }) {
    return (
        <div className="flex flex-col items-center justify-center py-12">
            <div className="w-28 h-28">
                <DotLottieReact src={lottieSrc} loop autoplay />
            </div>
            <p className="mt-4 text-gray-600 font-medium">{message}</p>
        </div>
    );
}

const Badge = ({ children, tone = "slate", size = "md" }) => {
    const sizeClasses = {
        sm: "px-2 py-0.5 text-[0.75em]",
        md: "px-2.5 py-1 text-[0.85em]",
        lg: "px-3 py-1.5 text-[0.9em]",
    };
    
    const toneClasses = {
        slate: "bg-slate-100 text-slate-800 border-slate-200",
        blue: "bg-blue-100 text-blue-800 border-blue-200",
        violet: "bg-violet-100 text-violet-800 border-violet-200",
        emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
        amber: "bg-amber-100 text-amber-800 border-amber-200",
        rose: "bg-rose-100 text-rose-800 border-rose-200",
    };
    
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full font-medium border ${sizeClasses[size]} ${toneClasses[tone]}`}
        >
            {children}
        </span>
    );
};

const SoftButton = ({ className = "", children, variant = "default", ...rest }) => {
    const variants = {
        default: "bg-white/80 backdrop-blur border border-gray-200 hover:border-gray-300 text-gray-700",
        primary: "bg-blue-600 text-white border-0 shadow-md",
        success: "bg-green-600 text-white border-0 shadow-md",
        danger: "bg-red-600 text-white border-0 shadow-md",
    };
    
    return (
        <button
            className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold hover:shadow-lg active:scale-[.98] transition-all duration-200 text-[0.95em] ${variants[variant]} ${className}`}
            {...rest}
        >
            {children}
        </button>
    );
};

// Toast notification component
const Toast = ({ message, type = "success", onClose }) => {
    const icons = {
        success: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
        error: <AlertCircle className="w-5 h-5 text-rose-600" />,
        info: <Info className="w-5 h-5 text-blue-600" />,
    };
    
    const bgClasses = {
        success: "bg-emerald-50 border-emerald-200",
        error: "bg-rose-50 border-rose-200",
        info: "bg-blue-50 border-blue-200",
    };
    
    return (
        <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`fixed top-4 right-4 z-50 max-w-md rounded-xl border shadow-lg p-4 ${bgClasses[type]}`}
        >
            <div className="flex items-start gap-3">
                {icons[type]}
                <div className="flex-1">
                    <p className="font-medium text-gray-900">{message}</p>
                </div>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                    <XIcon className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, trend, trendValue, loading }) => (
    <motion.div
        whileHover={{ y: -2 }}
        className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all"
    >
        <div className="flex items-start justify-between">
            <div>
                <p className="text-gray-600 text-sm font-medium mb-1">{label}</p>
                {loading ? (
                    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
                ) : (
                    <p className="text-xl font-bold text-gray-900">{value}</p>
                )}
                {trend && trendValue && (
                    <div className="flex items-center gap-1 mt-2">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm text-emerald-600 font-medium">{trendValue}</span>
                    </div>
                )}
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
    </motion.div>
);

// Loading Skeleton
const TableSkeleton = () => (
    <div className="space-y-3 p-4">
        {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4 animate-pulse">
                <div className="h-12 w-12 bg-gray-200 rounded-lg" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
            </div>
        ))}
    </div>
);

async function svcSearchOrders(payload) {
    const { Authorization, merchantId } = getAuth();
    const { data } = await axios.post(API.searchOrders(), payload, {
        headers: {
            Authorization,
            "x-merchant-id": merchantId,
            "Content-Type": "application/json",
        },
    });
    return data;
}
async function svcCreateManifest(args) {
    const { Authorization } = getAuth();
    const { data } = await axios.post(API.createManifest(), args, {
        headers: { Authorization, "Content-Type": "application/json" },
    });
    return data;
}

async function svcGetManifestGeneratedUrl(manifestId) {
    const { Authorization } = getAuth();
    const url = API.getManifestPresignedUrl
        ? API.getManifestPresignedUrl(manifestId)
        : `/api/v1/manifests/${manifestId}/presigned-url`;
    const { data } = await axios.get(url, { headers: { Authorization } });
    return data?.presigned_url;
}

async function svcGetManifestSignedUrl(manifestId) {
    const { Authorization } = getAuth();
    const url = API.getSignedManifestPresignedUrl
        ? API.getSignedManifestPresignedUrl(manifestId)
        : `${API.getManifestPresignedUrl(manifestId)}?variant=signed`;
    const { data } = await axios.get(url, { headers: { Authorization } });
    return data?.presigned_url;
}

async function svcUploadSignedManifest(manifestId, file) {
    const { Authorization } = getAuth();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.post(API.uploadSignedManifest(manifestId), form, {
        headers: { Authorization },
    });
    return data;
}
async function svcSearchManifests(payload) {
    const { Authorization } = getAuth();
    const { data } = await axios.post(API.searchManifest(), payload, {
        headers: { Authorization, "Content-Type": "application/json" },
    });
    return data;
}

function ErrorBanner({ error, onClose }) {
    const [open, setOpen] = useState(false);
    if (!error) return null;
    return (
        <div className="w-full rounded-2xl border border-red-200/70 bg-red-50 text-red-800 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="font-semibold leading-tight">{error.title || "Error"}</div>
                        <div className="text-[0.9em] text-red-900/90">{error.message}</div>
                        {error.context ? (
                            <button
                                className="mt-2 inline-flex items-center gap-1 text-[0.8em] underline underline-offset-2"
                                onClick={() => setOpen((v) => !v)}
                            >
                                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                                Details
                            </button>
                        ) : null}
                    </div>
                </div>
                <button onClick={onClose} className="text-red-700/80 hover:text-red-900 text-sm">✕</button>
            </div>
            {open && error.context ? (
                <div className="mt-3 rounded-lg bg-white p-3 text-[0.85em] text-red-900 border border-red-100 overflow-x-auto">
                    <pre className="whitespace-pre-wrap break-words">{JSON.stringify(error.context, null, 2)}</pre>
                </div>
            ) : null}
        </div>
    );
}

function EmptyState({ icon: Icon = Search, title, subtitle }) {
    return (
        <div className="w-full py-16 flex flex-col items-center justify-center text-center">
            <Icon className="w-12 h-12 mb-3 text-gray-400" />
            <div className="text-[1.05em] font-semibold">{title}</div>
            {subtitle && <div className="text-[0.9em] text-gray-500">{subtitle}</div>}
        </div>
    );
}

export default function PickupManifest() {
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("pending"); // "pending" | "manifests"
    const [toast, setToast] = useState(null);
    const [viewMode, setViewMode] = useState(() => {
        // Default to cards for mobile (< 768px), table for desktop
        return typeof window !== 'undefined' && window.innerWidth < 768 ? "cards" : "table";
    }); // "table" | "cards"
    const [showFilters, setShowFilters] = useState(true);

    const [fontScale, setFontScale] = useState(1);
    const incFont = () => setFontScale((v) => Math.min(1.25, parseFloat((v + 0.05).toFixed(2))));
    const decFont = () => setFontScale((v) => Math.max(0.95, parseFloat((v - 0.05).toFixed(2))));
    
    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    // Default date windows (1 month)
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    /** Pending Pickups state */
    const [query, setQuery] = useState({
        orderReferenceNumber: "",
        awbNumber: "",
        createdRange: [oneMonthAgo, today],
        limit: DEFAULT_PAGE_SIZE,
        skip: 0,
    });
    const [qDebounced] = useDebounce(query, 300);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [carrierByName, setCarrierByName] = useState(new Map());
    const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i;

    useEffect(() => {
        try {
            const raw = localStorage.getItem("allocatedCarriers");
            const parsed = JSON.parse(raw || "null");

            // Support both shapes: raw array OR { allocated_carrier_details: [...] }
            const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.allocated_carrier_details)
                    ? parsed.allocated_carrier_details
                    : [];

            const temp = new Map();

            arr.forEach((c) => {
                const name = String(c?.carrier_partner_identifier_string ?? "").trim();
                const id = String(c?.carrier_partner_id ?? "").trim();
                if (!name || !id) return;

                const key = name.toUpperCase();
                const isReverse = reverseMatcher.test(id);
                const existing = temp.get(key);

                if (!existing) {
                    // first time we see this name
                    temp.set(key, { id, isReverse });
                    return;
                }
                if (existing.isReverse && !isReverse) {
                    temp.set(key, { id, isReverse });
                }
            });

            const finalMap = new Map();
            temp.forEach((val, key) => finalMap.set(key, val.id));

            setCarrierByName(finalMap);
        } catch {
            setCarrierByName(new Map());
        }
    }, []);


    /** Selection & lock */
    const [selected, setSelected] = useState(new Set());
    const [selectedRowsStore, setSelectedRowsStore] = useState(new Map());
    const [lockedCourierId, setLockedCourierId] = useState("");
    const [lockedCourierName, setLockedCourierName] = useState("");
    const [courierLockMessage, setCourierLockMessage] = useState("");

    /** Manifests state */
    const [mQuery, setMQuery] = useState({
        courier_partner_id: "",
        manifest_number: "",
        createdRange: [oneMonthAgo, today],
        page: 0,
        size: 20,
        sort_by: "created_date_time",
        sort_dir: "DESC",
    });
    const [manifests, setManifests] = useState([]);
    const [mTotal, setMTotal] = useState(0);
    const [mLoading, setMLoading] = useState(false);

    /** Row expansion & per-row AWB pagination */
    const [expandedManifestId, setExpandedManifestId] = useState(""); // controls inline expansion
    const [awbPageByManifest, setAwbPageByManifest] = useState({}); // { manifest_id: pageIndex }
    const [uploadBusyFor, setUploadBusyFor] = useState(""); // manifest_id currently uploading

    /** Helpers */
    const resolveCourierName = useCallback((row) => {
        return String(row?.allocated_carrier_partner_public_name ?? "").trim();
    }, []);
    const resolveCourierId = useCallback((row) => {
        const name = resolveCourierName(row);
        if (!name) return "";
        const id = carrierByName.get(name.toUpperCase());
        return String(id ?? "").trim();
    }, [carrierByName, resolveCourierName]);

    const selectedRows = useMemo(() => Array.from(selectedRowsStore.values()), [selectedRowsStore]);
    const selectableOnPage = useMemo(() => {
        return rows.filter((r) => {
            const id = resolveCourierId(r);
            return !lockedCourierId || (id && id === lockedCourierId);
        });
    }, [rows, lockedCourierId, resolveCourierId]);
    const allOnPageSelected =
        selectableOnPage.length > 0 && selectableOnPage.every((r) => selected.has(keyOf(r)));

    const formatToISTISO = (d, endOfDay = false) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const time = endOfDay ? "23:59:59" : "00:00:00";
        return `${yyyy}-${mm}-${dd}T${time}+05:30`;
    };
    const formatToUTCISO = (d, endOfDay = false) => {
        const dt = new Date(d);
        if (endOfDay) dt.setUTCHours(23, 59, 59, 0);
        else dt.setUTCHours(0, 0, 0, 0);
        return dt.toISOString().replace(/\.\d{3}Z$/, "Z");
    };
    const formatManifestCreatedAt = (isoString) => {
        if (!isoString) return "—";
        try {
            return new Date(isoString).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return isoString;
        }
    };

    /** Fetch orders */
    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const merchantId = localStorage.getItem("merchantId");
            const [from, to] = query.createdRange || [oneMonthAgo, today];

            const payload = {
                merchant_id: merchantId,
                order_reference_number: query.orderReferenceNumber || null,
                awb_number: query.awbNumber || null,
                payment_mode: null,
                customer_email: null,
                customer_phone: null,
                order_status: ALLOWED_MANIFEST_STATUS_CODES,
                from_shipment_order_creation_datetime_utc: formatToISTISO(from, false),
                to_shipment_order_creation_datetime_utc: formatToISTISO(to, true),
                limit: qDebounced.limit,
                skip: qDebounced.skip,
                sort_field: "shipment_order_date_time_utc",
                sort_direction: "DESC",
            };

            const data = await svcSearchOrders(payload);
            setRows(Array.isArray(data.orders) ? data.orders : []);
            setTotal(data.totalCount ?? 0);
        } catch (e) {
            setError(parseApiError(e));
        } finally {
            setLoading(false);
        }
    }, [qDebounced.limit, qDebounced.skip, query.orderReferenceNumber, query.awbNumber, query.createdRange]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const [carrierNameById, setCarrierNameById] = useState(new Map());
    useEffect(() => {
        try {
            const raw = localStorage.getItem("allocatedCarriers");
            const parsed = JSON.parse(raw || "null");

            const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.allocated_carrier_details)
                    ? parsed.allocated_carrier_details
                    : [];

            const map = new Map();
            arr.forEach((c) => {
                const id = String(c?.carrier_partner_id ?? "").trim();
                const name = String(c?.carrier_partner_identifier_string ?? "").trim();
                if (id && name) map.set(id, name);
            });
            setCarrierNameById(map);
        } catch {
            setCarrierNameById(new Map());
        }
    }, []);


    /** Selection logic with strict courier lock */
    const toggleRow = (row) => {
        const k = keyOf(row);
        const rowName = resolveCourierName(row);
        const rowId = resolveCourierId(row);
        const isSelected = selected.has(k);

        if (isSelected) {
            const next = new Set(selected);
            next.delete(k);

            const nextStore = new Map(selectedRowsStore);
            nextStore.delete(k);

            setSelected(next);
            setSelectedRowsStore(nextStore);

            if (next.size === 0) {
                setLockedCourierId("");
                setLockedCourierName("");
                setCourierLockMessage("");
            }
            return;
        }

        if (lockedCourierId && rowId && rowId !== lockedCourierId) {
            setCourierLockMessage(
                `You can only select orders of the same courier partner (${lockedCourierName}).`
            );
            return;
        }

        const next = new Set(selected);
        next.add(k);

        const nextStore = new Map(selectedRowsStore);
        nextStore.set(k, row); // keep snapshot of this row across pages

        setSelected(next);
        setSelectedRowsStore(nextStore);

        if (!lockedCourierId && rowId) {
            setLockedCourierId(rowId);
            setLockedCourierName(rowName || rowId);
            setCourierLockMessage("");
        }
    };


    const toggleAllOnPage = (checked) => {
        if (!checked) {
            const next = new Set(selected);
            const nextStore = new Map(selectedRowsStore);

            rows.forEach((r) => {
                const k = keyOf(r);
                next.delete(k);
                nextStore.delete(k);
            });

            setSelected(next);
            setSelectedRowsStore(nextStore);

            if (next.size === 0) {
                setLockedCourierId("");
                setLockedCourierName("");
                setCourierLockMessage("");
            }
            return;
        }

        let lockId = lockedCourierId;
        let lockName = lockedCourierName;

        if (!lockId) {
            const first = rows.find((r) => !!resolveCourierId(r));
            if (first) {
                lockId = resolveCourierId(first);
                lockName = resolveCourierName(first) || lockId;
                setLockedCourierId(lockId);
                setLockedCourierName(lockName);
                setCourierLockMessage("");
            }
        }

        const next = new Set(selected);
        const nextStore = new Map(selectedRowsStore);

        rows.forEach((r) => {
            const rc = resolveCourierId(r);
            if (!lockId || (rc && rc === lockId)) {
                const k = keyOf(r);
                next.add(k);
                nextStore.set(k, r);
            }
        });

        setSelected(next);
        setSelectedRowsStore(nextStore);

        if (lockId) {
            const mixed = rows.some((r) => resolveCourierId(r) && resolveCourierId(r) !== lockId);
            setCourierLockMessage(
                mixed ? `Only shipments from courier ${lockName} are included.` : ""
            );
        }
    };


    const clearSelection = () => {
        setSelected(new Set());
        setSelectedRowsStore(new Map());
        setLockedCourierId("");
        setLockedCourierName("");
        setCourierLockMessage("");
    };

    /** Generate Manifest */
    const [generating, setGenerating] = useState(false);
    const allSelectedHaveAwb = selectedRows.every((r) => !!r.awb_number);
    const canGenerate = selected.size > 0 && !!lockedCourierId && allSelectedHaveAwb;
    const generateReason =
        selected.size === 0
            ? "Select at least one shipment"
            : !lockedCourierId
                ? "Courier not determined from selection"
                : !allSelectedHaveAwb
                    ? "All selected shipments must have AWB"
                    : "";

    const handleGenerateManifest = async () => {
        if (!canGenerate) return;
        try {
            setGenerating(true);
            setError(null);
            const merchant_id = localStorage.getItem("merchantId") || "";
            const awb_numbers = selectedRows.map((r) => String(r.awb_number));
            const resp = await svcCreateManifest({
                merchant_id,
                courier_partner_id: lockedCourierId,
                awb_numbers,
                idempotency_key: idemKey(),
            });
            if (resp?.presigned_url) {
                window.open(resp.presigned_url, "_blank", "noopener,noreferrer");
                showToast(`Manifest generated successfully with ${awb_numbers.length} shipments!`, "success");
            }
            clearSelection();
            fetchOrders(); // Refresh data
        } catch (e) {
            setError(parseApiError(e));
            showToast("Failed to generate manifest", "error");
        } finally {
            setGenerating(false);
        }
    };
    
    // Calculate stats
    const stats = useMemo(() => {
        const withAwb = rows.filter(r => r.awb_number).length;
        const uniqueCouriers = new Set(rows.map(r => resolveCourierName(r)).filter(Boolean)).size;
        return {
            total: rows.length,
            withAwb,
            selected: selected.size,
            couriers: uniqueCouriers,
        };
    }, [rows, selected.size, resolveCourierName]);

    /** Fetch manifests (uses createdRange via UTC Z times) */
    const fetchManifests = useCallback(async () => {
        try {
            setMLoading(true);
            setError(null);
            const merchant_id = localStorage.getItem("merchantId") || "";
            const [mFrom, mTo] = mQuery.createdRange || [oneMonthAgo, today];

            const payload = {
                merchant_id,
                courier_partner_id: mQuery.courier_partner_id || null,
                manifest_number: mQuery.manifest_number || null,
                from_created_date: formatToUTCISO(mFrom, false),
                to_created_date: formatToUTCISO(mTo, true),
                page: mQuery.page,
                size: mQuery.size,
                sort_by: mQuery.sort_by,
                sort_dir: mQuery.sort_dir,
            };

            const data = await svcSearchManifests(payload);
            setManifests(Array.isArray(data.manifests) ? data.manifests : []);
            setMTotal(Number.isFinite(data.total) ? data.total : 0);
        } catch (e) {
            setError(parseApiError(e));
            setManifests([]);
            setMTotal(0);
        } finally {
            setMLoading(false);
        }
    }, [mQuery]);

    useEffect(() => {
        if (tab === "manifests") fetchManifests();
    }, [tab, fetchManifests, mQuery.createdRange, mQuery.page, mQuery.size, mQuery.sort_by, mQuery.sort_dir]);

    const totalPages = Math.max(1, Math.ceil(total / query.limit));
    const mTotalPages = Math.max(1, Math.ceil(mTotal / (mQuery.size || 1)));

    const fileInputRefs = useRef({});
    const ensureFileInputRef = (id) => {
        if (!fileInputRefs.current[id]) fileInputRefs.current[id] = React.createRef();
        return fileInputRefs.current[id];
    };

    const handleChooseSigned = (m) => {
        const proceed = window.confirm(
            `Upload signed file for Manifest ${m.manifest_number}\n\nCourier: ${carrierNameById.get(m.courier_partner_id)}\nManifest ID: ${m.manifest_id}\n\nIs this correct?`
        );
        if (!proceed) return;
        ensureFileInputRef(m.manifest_id).current?.click();
    };

    const handleUploadSignedFor = async (m, file) => {
        if (!m?.manifest_id || !file) return;
        try {
            setUploadBusyFor(m.manifest_id);
            setError(null);
            await svcUploadSignedManifest(m.manifest_id, file);
            showToast(`Signed manifest uploaded successfully for ${m.manifest_number}`, "success");
            fetchManifests(); // Refresh manifest list
        } catch (e) {
            setError(parseApiError(e));
            showToast("Failed to upload signed manifest", "error");
        } finally {
            setUploadBusyFor("");
            const ref = ensureFileInputRef(m.manifest_id);
            if (ref?.current) ref.current.value = "";
        }
    };

    return (
        <div
            className="w-full min-h-[calc(100vh-88px)] bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20"
            style={{ fontSize: `${16 * fontScale}px`, lineHeight: 1.5 }}
        >
            {/* Toast Notifications */}
            <AnimatePresence>
                {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            </AnimatePresence>

            <div className="px-4 py-2 md:py-2 space-y-8 max-w-[1600px]">
                <ErrorBanner error={error} onClose={() => setError(null)} />


                {/* Stats Dashboard - Only show on pending tab */}
                {tab === "pending" && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                    >
                        <StatsCard
                            icon={Package}
                            label="Total Shipments"
                            value={stats.total}
                            loading={loading}
                        />
                        <StatsCard
                            icon={CheckCircle2}
                            label="With AWB"
                            value={stats.withAwb}
                            loading={loading}
                        />
                        <StatsCard
                            icon={Rows3}
                            label="Selected"
                            value={stats.selected}
                            loading={false}
                        />
                        <StatsCard
                            icon={Truck}
                            label="Courier Partners"
                            value={stats.couriers}
                            loading={loading}
                        />
                    </motion.div>
                )}

                {courierLockMessage && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3 shadow-sm"
                    >
                        <div className="p-2 bg-amber-100 rounded-xl">
                            <AlertCircle className="w-5 h-5 text-amber-700" />
                        </div>
                        <span className="text-amber-900 font-medium">{courierLockMessage}</span>
                    </motion.div>
                )}

                {/* Enhanced Tabs */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2">
                    <div className="flex gap-2">
                        <button
                            className={`flex-1 px-6 py-3.5 rounded-xl font-semibold whitespace-nowrap inline-flex items-center justify-center gap-2 transition-all ${
                                tab === "pending"
                                    ? "bg-blue-600 text-white shadow-md"
                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => setTab("pending")}
                        >
                            <Truck className="w-5 h-5" />
                            Pending Pickups
                            {tab === "pending" && <Badge tone="blue" size="sm">{total}</Badge>}
                        </button>
                        <button
                            className={`flex-1 px-6 py-3.5 rounded-xl font-semibold whitespace-nowrap inline-flex items-center justify-center gap-2 transition-all ${
                                tab === "manifests"
                                    ? "bg-blue-600 text-white shadow-md"
                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => setTab("manifests")}
                        >
                            <ListChecks className="w-5 h-5" />
                            Manifests
                            {tab === "manifests" && <Badge tone="blue" size="sm">{mTotal}</Badge>}
                        </button>
                    </div>
                </div>

                {/* PENDING PICKUPS */}
                {tab === "pending" && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/95 backdrop-blur rounded-3xl border border-gray-200 shadow-xl overflow-hidden"
                    >
                        {/* Enhanced Filter Bar */}
                        <div className="border-b border-gray-200 bg-gray-50">
                            <div className="px-6 py-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 rounded-xl">
                                            <Filter className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900">Filters & Search</h3>
                                        <button
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="ml-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                                        >
                                            {showFilters ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            {showFilters ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">View:</span>
                                        <div className="flex bg-gray-100 rounded-xl p-1">
                                            <button
                                                onClick={() => setViewMode("table")}
                                                className={`px-3 py-1.5 rounded-lg transition-all ${
                                                    viewMode === "table"
                                                        ? "bg-white shadow-sm text-blue-600"
                                                        : "text-gray-600 hover:text-gray-900"
                                                }`}
                                            >
                                                <LayoutList className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setViewMode("cards")}
                                                className={`px-3 py-1.5 rounded-lg transition-all ${
                                                    viewMode === "cards"
                                                        ? "bg-white shadow-sm text-blue-600"
                                                        : "text-gray-600 hover:text-gray-900"
                                                }`}
                                            >
                                                <Grid3x3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {showFilters && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                                                <div className="lg:col-span-3">
                                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                                        <HashIcon className="w-4 h-4 text-blue-600" />
                                                        Order Reference
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={query.orderReferenceNumber}
                                                        onChange={(e) =>
                                                            setQuery((p) => ({ ...p, orderReferenceNumber: e.target.value, skip: 0 }))
                                                        }
                                                        placeholder="ORD-123..."
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition placeholder:text-gray-400"
                                                    />
                                                </div>

                                                <div className="lg:col-span-3">
                                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                                        <ScanLine className="w-4 h-4 text-blue-600" />
                                                        AWB Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={query.awbNumber}
                                                        onChange={(e) => setQuery((p) => ({ ...p, awbNumber: e.target.value, skip: 0 }))}
                                                        placeholder="PRZP000..."
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition placeholder:text-gray-400"
                                                    />
                                                </div>

                                                <div className="lg:col-span-4">
                                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                                        <CalendarClock className="w-4 h-4 text-blue-600" />
                                                        Created Date Range
                                                    </label>
                                                    <CustomDateRangePicker
                                                        selectedRange={query.createdRange}
                                                        onRangeChange={(range) => setQuery((p) => ({ ...p, createdRange: range, skip: 0 }))}
                                                    />
                                                </div>

                                                <div className="lg:col-span-2 flex flex-col sm:flex-row gap-2">
                                                    <SoftButton
                                                        onClick={fetchOrders}
                                                        variant="primary"
                                                        className="flex-1"
                                                    >
                                                        <Search className="w-4 h-4" /> Search
                                                    </SoftButton>
                                                    <button
                                                        onClick={() =>
                                                            setQuery({
                                                                orderReferenceNumber: "",
                                                                awbNumber: "",
                                                                createdRange: [oneMonthAgo, today],
                                                                limit: DEFAULT_PAGE_SIZE,
                                                                skip: 0,
                                                            })
                                                        }
                                                        className="px-4 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-all"
                                                    >
                                                        <RefreshCw className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Selection Summary & Actions */}
                        <AnimatePresence>
                            {selected.size > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="border-b border-gray-200 bg-blue-50"
                                >
                                    <div className="px-6 py-4">
                                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <Badge tone="blue" size="lg">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    {selected.size} Selected
                                                </Badge>
                                                {lockedCourierName && (
                                                    <Badge tone="violet" size="lg">
                                                        <Package className="w-4 h-4" />
                                                        {lockedCourierName}
                                                    </Badge>
                                                )}
                                                {!canGenerate && generateReason && (
                                                    <span className="inline-flex items-center gap-2 text-sm text-amber-700">
                                                        <Info className="w-4 h-4" />
                                                        {generateReason}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={clearSelection}
                                                    className="px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                                                >
                                                    <XIcon className="w-4 h-4 inline mr-1" />
                                                    Clear
                                                </button>
                                                <SoftButton
                                                    onClick={handleGenerateManifest}
                                                    disabled={!canGenerate || generating}
                                                    variant={canGenerate ? "primary" : "default"}
                                                    className={`min-w-[180px] ${
                                                        !canGenerate || generating ? "opacity-50 cursor-not-allowed" : ""
                                                    }`}
                                                >
                                                    <ShieldCheck className="w-5 h-5" />
                                                    {generating ? "Generating..." : `Generate Manifest`}
                                                    {canGenerate && <ArrowUpRight className="w-4 h-4" />}
                                                </SoftButton>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Data Display - Table or Cards */}
                        <div className="p-6">
                            {loading ? (
                                <TableSkeleton />
                            ) : rows.length === 0 ? (
                                <EmptyState
                                    icon={Search}
                                    title="No shipments found"
                                    subtitle="Try adjusting your filters or date range."
                                />
                            ) : viewMode === "table" ? (
                                <div className="overflow-x-auto rounded-xl border border-gray-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr className="text-left text-xs tracking-wide text-gray-700 uppercase font-bold">
                                                <th className="p-4 w-12">
                                                    <input
                                                        type="checkbox"
                                                        className="h-5 w-5 accent-blue-600 cursor-pointer rounded transition-all"
                                                        checked={allOnPageSelected}
                                                        onChange={(e) => toggleAllOnPage(e.target.checked)}
                                                        title="Select all on this page"
                                                    />
                                                </th>
                                                <th className="p-4">
                                                    <div className="inline-flex items-center gap-2">
                                                        <HashIcon className="w-4 h-4" />
                                                        Order Info
                                                    </div>
                                                </th>
                                                <th className="p-4">
                                                    <div className="inline-flex items-center gap-2">
                                                        <ScanLine className="w-4 h-4" />
                                                        AWB
                                                    </div>
                                                </th>
                                                <th className="p-4">
                                                    <div className="inline-flex items-center gap-2">
                                                        <Package className="w-4 h-4" />
                                                        Courier
                                                    </div>
                                                </th>
                                                <th className="p-4">
                                                    <div className="inline-flex items-center gap-2">
                                                        <MapPin className="w-4 h-4" />
                                                        Pickup & Delivery
                                                    </div>
                                                </th>
                                                <th className="p-4">
                                                    <div className="inline-flex items-center gap-2">
                                                        <Zap className="w-4 h-4" />
                                                        Status
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {rows.map((r, i) => {
                                                const checked = selected.has(keyOf(r));
                                                const createdDisplay = r.shipment_order_date_time_utc
                                                    ? new Date(r.shipment_order_date_time_utc).toLocaleString("en-IN", {
                                                        timeZone: "Asia/Kolkata",
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })
                                                    : r.order_placed_date_time || "—";
                                                const courierName = resolveCourierName(r);
                                                return (
                                                    <motion.tr
                                                        key={i}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: i * 0.02 }}
                                                        className={`hover:bg-blue-50/50 transition-colors ${
                                                            checked ? "bg-blue-50/30" : ""
                                                        }`}
                                                    >
                                                        <td className="p-4">
                                                            <input
                                                                type="checkbox"
                                                                className="h-5 w-5 accent-blue-600 cursor-pointer rounded transition-all"
                                                                checked={checked}
                                                                onChange={() => toggleRow(r)}
                                                                title={checked ? "Unselect" : "Select"}
                                                            />
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="flex flex-col gap-1">
                                                                <span className="font-semibold text-gray-900">
                                                                    {r.order_reference_number}
                                                                </span>
                                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                                    <Clock className="w-3 h-3" />
                                                                    {createdDisplay}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            {r.awb_number ? (
                                                                <Badge tone="blue">{r.awb_number}</Badge>
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="p-2 bg-violet-100 rounded-lg">
                                                                    <Package className="w-4 h-4 text-violet-600" />
                                                                </div>
                                                                <span className="font-medium text-gray-900">
                                                                    {courierName || "—"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="space-y-2 max-w-xs">
                                                                <div className="flex items-start gap-2">
                                                                    <MapPin className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 text-xs">
                                                                            {r.pickup_organisation}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {r.pickup_city} — {r.pickup_pincode}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start gap-2">
                                                                    <UserCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 text-xs">
                                                                            {r.drop_name}
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {r.drop_city}, {r.drop_state} — {r.drop_pincode}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <Badge tone="emerald">
                                                                {getOrderStatusName(r.latest_order_status_code)}
                                                            </Badge>
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                // Card View
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {rows.map((r, i) => {
                                        const checked = selected.has(keyOf(r));
                                        const createdDisplay = r.shipment_order_date_time_utc
                                            ? new Date(r.shipment_order_date_time_utc).toLocaleString("en-IN", {
                                                timeZone: "Asia/Kolkata",
                                                year: "numeric",
                                                month: "short",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })
                                            : r.order_placed_date_time || "—";
                                        const courierName = resolveCourierName(r);
                                        return (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                whileHover={{ y: -4 }}
                                                className={`group relative rounded-2xl border-2 p-5 cursor-pointer transition-all ${
                                                    checked
                                                        ? "border-blue-500 bg-blue-50 shadow-lg"
                                                        : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-md"
                                                }`}
                                                onClick={() => toggleRow(r)}
                                            >
                                                {/* Selection Indicator */}
                                                <div className="absolute top-4 right-4">
                                                    <div
                                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                                            checked
                                                                ? "border-blue-600 bg-blue-600"
                                                                : "border-gray-300 bg-white group-hover:border-blue-400"
                                                        }`}
                                                    >
                                                        {checked && <CheckCircle2 className="w-4 h-4 text-white" />}
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    {/* Header */}
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 mb-1 pr-8">
                                                            {r.order_reference_number}
                                                        </h4>
                                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {createdDisplay}
                                                        </p>
                                                    </div>

                                                    {/* AWB */}
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                            AWB
                                                        </label>
                                                        {r.awb_number ? (
                                                            <Badge tone="blue">{r.awb_number}</Badge>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">Not assigned</span>
                                                        )}
                                                    </div>

                                                    {/* Courier */}
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                            Courier
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                            <Package className="w-4 h-4 text-violet-600" />
                                                            <span className="text-sm font-medium">{courierName || "—"}</span>
                                                        </div>
                                                    </div>

                                                    {/* Locations */}
                                                    <div className="space-y-2 pt-2 border-t border-gray-200">
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                                                <MapPin className="w-3 h-3 text-emerald-600" />
                                                                Pickup
                                                            </label>
                                                            <div className="text-sm">
                                                                <div className="font-medium text-gray-900">
                                                                    {r.pickup_organisation}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {r.pickup_city} — {r.pickup_pincode}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                                                <UserCircle2 className="w-3 h-3 text-blue-600" />
                                                                Delivery
                                                            </label>
                                                            <div className="text-sm">
                                                                <div className="font-medium text-gray-900">{r.drop_name}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {r.drop_city}, {r.drop_state} — {r.drop_pincode}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Status */}
                                                    <div className="pt-2">
                                                        <Badge tone="emerald">
                                                            {getOrderStatusName(r.latest_order_status_code)}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Enhanced Pagination */}
                        <div className="px-6 py-5 border-t border-gray-200 bg-gray-50">
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setQuery((prev) => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                                        disabled={query.skip === 0}
                                        className="px-5 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        <ChevronDown className="w-4 h-4 rotate-90" />
                                        Previous
                                    </button>
                                    <div className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold shadow-md">
                                        Page {Math.floor(query.skip / query.limit) + 1}
                                        {totalPages > 1 && <span className="font-normal"> / {totalPages}</span>}
                                    </div>
                                    <button
                                        onClick={() =>
                                            rows.length === query.limit && setQuery((prev) => ({ ...prev, skip: prev.skip + prev.limit }))
                                        }
                                        disabled={rows.length < query.limit}
                                        className="px-5 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        Next
                                        <ChevronDown className="w-4 h-4 -rotate-90" />
                                    </button>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="font-semibold text-gray-700 flex items-center gap-2">
                                        <Rows3 className="w-5 h-5 text-blue-600" />
                                        Rows per page
                                    </label>
                                    <select
                                        value={query.limit}
                                        onChange={(e) => setQuery((p) => ({ ...p, limit: +e.target.value, skip: 0 }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-xl bg-white shadow-sm hover:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-semibold"
                                    >
                                        {[10, 20, 50, 100].map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* MANIFESTS */}
                {tab === "manifests" && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/95 backdrop-blur rounded-3xl border border-gray-200 shadow-xl overflow-hidden"
                    >
                        {/* Enhanced Search Form */}
                        <div className="px-6 py-6 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="p-2 bg-blue-100 rounded-xl">
                                    <ListChecks className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Search Manifests</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                        <Package className="w-4 h-4 text-blue-600" />
                                        Courier Partner ID
                                    </label>
                                    <input
                                        type="text"
                                        value={mQuery.courier_partner_id}
                                        onChange={(e) => setMQuery((p) => ({ ...p, courier_partner_id: e.target.value, page: 0 }))}
                                        placeholder="PRZ_EKART..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                        <HashIcon className="w-4 h-4 text-blue-600" />
                                        Manifest Number
                                    </label>
                                    <input
                                        type="text"
                                        value={mQuery.manifest_number}
                                        onChange={(e) => setMQuery((p) => ({ ...p, manifest_number: e.target.value, page: 0 }))}
                                        placeholder="MN-2025..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                        <CalendarClock className="w-4 h-4 text-blue-600" />
                                        Created Date Range
                                    </label>
                                    <CustomDateRangePicker
                                        selectedRange={mQuery.createdRange}
                                        onRangeChange={(range) => setMQuery((p) => ({ ...p, createdRange: range, page: 0 }))}
                                    />
                                </div>
                                <div className="flex items-end gap-3">
                                    <SoftButton onClick={fetchManifests} variant="primary" className="flex-1">
                                        <Search className="w-4 h-4" /> Search
                                    </SoftButton>
                                    <button
                                        onClick={() =>
                                            setMQuery({
                                                courier_partner_id: "",
                                                manifest_number: "",
                                                createdRange: [oneMonthAgo, today],
                                                page: 0,
                                                size: 20,
                                                sort_by: "created_date_time",
                                                sort_dir: "DESC",
                                            })
                                        }
                                        className="px-4 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-all"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Results */}
                        <div className="p-6">
                            {mLoading ? (
                                <TableSkeleton />
                            ) : manifests.length === 0 ? (
                                <EmptyState
                                    icon={ListChecks}
                                    title="No manifests found"
                                    subtitle="Try different filters or date range."
                                />
                            ) : (
                                <div className="space-y-4">
                                    {manifests.map((m, idx) => {
                                        const isExpanded = expandedManifestId === m.manifest_id;
                                        const awbs = Array.isArray(m.awb_numbers) ? m.awb_numbers : [];
                                        const page = awbPageByManifest[m.manifest_id] ?? 0;
                                        const totalAwbPages = Math.max(1, Math.ceil(awbs.length / MANIFEST_AWB_PAGE_SIZE));
                                        const start = page * MANIFEST_AWB_PAGE_SIZE;
                                        const end = Math.min(start + MANIFEST_AWB_PAGE_SIZE, awbs.length);
                                        const pageSlice = awbs.slice(start, end);
                                        const isSigned = String(m?.status || "").toUpperCase() === "SIGNED";

                                        return (
                                            <motion.div
                                                key={m.manifest_id || idx}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.05 }}
                                                className={`rounded-2xl border-2 overflow-hidden transition-all ${
                                                    isExpanded
                                                        ? "border-blue-500 shadow-xl"
                                                        : "border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                                                }`}
                                            >
                                                {/* Card Header */}
                                                <div className={`p-6 ${isExpanded ? "bg-blue-50" : "bg-white"}`}>
                                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                        {/* Info */}
                                                        <div className="flex-1 space-y-3">
                                                            <div className="flex items-start gap-3">
                                                                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                                                                    <ListChecks className="w-6 h-6 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                                                                        {m.manifest_number}
                                                                    </h3>
                                                                    <p className="text-sm text-gray-600 flex items-center gap-2">
                                                                        <Clock className="w-4 h-4" />
                                                                        {formatManifestCreatedAt(m.created_date_time)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                <Badge tone="blue">
                                                                    <Package className="w-3 h-3" />
                                                                    {carrierNameById.get(m.courier_partner_id) || m.courier_partner_id}
                                                                </Badge>
                                                                <Badge tone="violet">
                                                                    <Rows3 className="w-3 h-3" />
                                                                    {awbs.length} AWBs
                                                                </Badge>
                                                                <Badge tone={isSigned ? "emerald" : "amber"}>
                                                                    {isSigned ? (
                                                                        <>
                                                                            <CheckCircle2 className="w-3 h-3" />
                                                                            SIGNED
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Clock className="w-3 h-3" />
                                                                            {m?.status || "PENDING"}
                                                                        </>
                                                                    )}
                                                                </Badge>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex flex-wrap gap-2">
                                                            <SoftButton
                                                                onClick={() => setExpandedManifestId(isExpanded ? "" : m.manifest_id)}
                                                                variant={isExpanded ? "default" : "primary"}
                                                            >
                                                                {isExpanded ? (
                                                                    <>
                                                                        <EyeOff className="w-4 h-4" /> Hide Details
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Eye className="w-4 h-4" /> View Details
                                                                    </>
                                                                )}
                                                            </SoftButton>

                                                            <SoftButton
                                                                onClick={async () => {
                                                                    try {
                                                                        const url = await svcGetManifestGeneratedUrl(m.manifest_id);
                                                                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                                                                    } catch (e) {
                                                                        setError(parseApiError(e));
                                                                    }
                                                                }}
                                                                variant="success"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                                Generated
                                                            </SoftButton>

                                                            {isSigned && (
                                                                <SoftButton
                                                                    onClick={async () => {
                                                                        try {
                                                                            const url = await svcGetManifestSignedUrl(m.manifest_id);
                                                                            if (url) window.open(url, "_blank", "noopener,noreferrer");
                                                                        } catch (e) {
                                                                            setError(parseApiError(e));
                                                                        }
                                                                    }}
                                                                    variant="success"
                                                                >
                                                                    <Download className="w-4 h-4" />
                                                                    Signed
                                                                </SoftButton>
                                                            )}

                                                            {!isSigned && (
                                                                <>
                                                                    <input
                                                                        ref={ensureFileInputRef(m.manifest_id)}
                                                                        type="file"
                                                                        className="hidden"
                                                                        onChange={(e) =>
                                                                            e.target.files?.[0] && handleUploadSignedFor(m, e.target.files[0])
                                                                        }
                                                                    />
                                                                    <SoftButton
                                                                        onClick={() => handleChooseSigned(m)}
                                                                        disabled={!!uploadBusyFor && uploadBusyFor === m.manifest_id}
                                                                        variant="default"
                                                                        className={uploadBusyFor === m.manifest_id ? "opacity-50" : ""}
                                                                    >
                                                                        <FileUp className="w-4 h-4" />
                                                                        {uploadBusyFor === m.manifest_id ? "Uploading..." : "Upload Signed"}
                                                                    </SoftButton>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <motion.div
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: "auto" }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            className="border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6"
                                                        >
                                                            {/* Metadata Grid */}
                                                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-6">
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        Manifest #
                                                                    </label>
                                                                    <p className="font-bold text-gray-900">{m.manifest_number}</p>
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        Manifest ID
                                                                    </label>
                                                                    <p className="font-mono text-sm text-gray-700">{m.manifest_id}</p>
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        Courier
                                                                    </label>
                                                                    <p className="font-medium text-gray-900">
                                                                        {carrierNameById.get(m.courier_partner_id) || m.courier_partner_id}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        AWBs
                                                                    </label>
                                                                    <p className="font-bold text-blue-600">{awbs.length}</p>
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        Status
                                                                    </label>
                                                                    <Badge tone={isSigned ? "emerald" : "amber"}>
                                                                        {isSigned ? "SIGNED" : m?.status || "—"}
                                                                    </Badge>
                                                                </div>
                                                                <div>
                                                                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                                                                        Created
                                                                    </label>
                                                                    <p className="text-sm text-gray-700">
                                                                        {formatManifestCreatedAt(m.created_date_time)}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            {/* AWB List */}
                                                            <div className="rounded-2xl bg-white border border-gray-200 shadow-inner p-5">
                                                                <div className="flex items-center justify-between mb-4">
                                                                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                                                        <ScanLine className="w-4 h-4 text-blue-600" />
                                                                        AWB Numbers
                                                                    </h4>
                                                                    {awbs.length > 0 && (
                                                                        <span className="text-sm text-gray-600">
                                                                            Showing {start + 1}–{end} of {awbs.length}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {awbs.length ? (
                                                                    <>
                                                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
                                                                            {pageSlice.map((awb, i) => (
                                                                                <motion.div
                                                                                    key={`${m.manifest_id}-${start + i}`}
                                                                                    initial={{ opacity: 0, scale: 0.9 }}
                                                                                    animate={{ opacity: 1, scale: 1 }}
                                                                                    transition={{ delay: i * 0.02 }}
                                                                                    className="px-3 py-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group"
                                                                                    title={awb}
                                                                                >
                                                                                    <p className="text-xs font-mono font-semibold text-gray-900 truncate group-hover:text-blue-600">
                                                                                        {awb}
                                                                                    </p>
                                                                                </motion.div>
                                                                            ))}
                                                                        </div>

                                                                        {totalAwbPages > 1 && (
                                                                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                                                                <button
                                                                                    onClick={() =>
                                                                                        setAwbPageByManifest((prev) => ({
                                                                                            ...prev,
                                                                                            [m.manifest_id]: Math.max(0, page - 1),
                                                                                        }))
                                                                                    }
                                                                                    disabled={page === 0}
                                                                                    className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                                >
                                                                                    ← Previous
                                                                                </button>
                                                                                <span className="text-sm text-gray-600">
                                                                                    Page <span className="font-bold">{page + 1}</span> of{" "}
                                                                                    <span className="font-bold">{totalAwbPages}</span>
                                                                                </span>
                                                                                <button
                                                                                    onClick={() =>
                                                                                        setAwbPageByManifest((prev) => ({
                                                                                            ...prev,
                                                                                            [m.manifest_id]: Math.min(totalAwbPages - 1, page + 1),
                                                                                        }))
                                                                                    }
                                                                                    disabled={page + 1 >= totalAwbPages}
                                                                                    className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                                                >
                                                                                    Next →
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <p className="text-center text-gray-500 py-8">No AWB numbers found</p>
                                                                )}
                                                            </div>

                                                            {/* Hint */}
                                                            <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
                                                                <div className="flex items-start gap-2">
                                                                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                                    <p>
                                                                        {isSigned
                                                                            ? "This manifest has been signed. Download using the 'Signed' button above."
                                                                            : `Upload a signed version by clicking 'Upload Signed' button for ${m.manifest_number}.`}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Enhanced Pagination */}
                        <div className="px-6 py-5 border-t border-gray-200 bg-gray-50">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="text-sm text-gray-600">
                                    Showing page <span className="font-bold text-gray-900">{(mQuery.page || 0) + 1}</span> of{" "}
                                    <span className="font-bold text-gray-900">{mTotalPages}</span> — Total{" "}
                                    <span className="font-bold text-gray-900">{mTotal}</span> manifests
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setMQuery((p) => ({ ...p, page: Math.max(0, (p.page || 0) - 1) }))}
                                        disabled={(mQuery.page || 0) === 0}
                                        className="px-5 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        <ChevronDown className="w-4 h-4 rotate-90" />
                                        Previous
                                    </button>
                                    <select
                                        value={mQuery.size}
                                        onChange={(e) => setMQuery((p) => ({ ...p, size: +e.target.value, page: 0 }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-xl bg-white shadow-sm hover:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-semibold"
                                    >
                                        {[10, 20, 50, 100].map((n) => (
                                            <option key={n} value={n}>
                                                {n}/page
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() =>
                                            (mQuery.page || 0) + 1 < mTotalPages && setMQuery((p) => ({ ...p, page: (p.page || 0) + 1 }))
                                        }
                                        disabled={(mQuery.page || 0) + 1 >= mTotalPages}
                                        className="px-5 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        Next
                                        <ChevronDown className="w-4 h-4 -rotate-90" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
